import { useMeetingRecordingStore } from "@/stores/use-meeting-recording-store"
import * as Sentry from "@sentry/nextjs"

import { ApiContext } from "@/hooks/api/base"
import {
  abandonSession,
  createLiveRecordingSession,
  finalizeChunks,
} from "@/hooks/api/meetings/live-recording-sessions/api"

import { BatchUploadProgress, uploadChunk, uploadChunkBatch } from "./chunk-uploader"
import {
  createSession,
  deleteSessionAndChunks,
  getChunkCount,
  getPendingChunks,
  getSession,
  reclaimUploadedChunkData,
  RecordingCaptureMetadata,
  recordingDb,
  saveChunk,
  updateSession,
} from "./recording-db"
import { acquireRecordingLock } from "./recording-lock"
import {
  describeRecordingStorageError,
  estimateRecordingStorage,
  isQuotaExceededError,
  RecordingPersistenceDiagnostics,
  RecordingPersistenceError,
} from "./recording-storage"

export type UploadProgress = {
  totalChunks: number
  uploadedChunks: number
  pendingChunks: number
  lastConfirmedSequence?: number
  status: "recording" | "uploading" | "finalizing" | "completed" | "failed"
  full?: boolean
}

export type RecordingFinalizationResult = {
  sessionId: string
  outcome: "abandoned" | "finalized"
}

export class RecordingResilientUploader {
  private meetingId: string
  private meetingTitle: string | null
  private getContext: () => Promise<ApiContext | null>
  private contentType: string
  private sessionId: string | null = null
  private sessionGuid: string | null = null
  private chunkSequence = 0
  private lastConfirmedSequence = -1
  private aborted = false
  private full = false
  private ingestFailure: RecordingPersistenceError | null = null
  private ingestQueue: Promise<void> = Promise.resolve()
  private persistedChunkCount = 0
  private uploadQueue: Promise<void> = Promise.resolve()
  private onProgress?: (progress: UploadProgress) => void
  private releaseLock?: () => Promise<void>

  constructor(opts: {
    meetingId: string
    meetingTitle?: string | null
    getContext: () => Promise<ApiContext | null>
    contentType: string
    onProgress?: (progress: UploadProgress) => void
  }) {
    this.meetingId = opts.meetingId
    this.meetingTitle = opts.meetingTitle ?? null
    this.getContext = opts.getContext
    this.contentType = opts.contentType
    this.onProgress = opts.onProgress
  }

  async start(): Promise<void> {
    const ctx = await this.requireContext()

    try {
      await reclaimUploadedChunkData()
    } catch (error) {
      Sentry.captureException(error, {
        level: "warning",
        tags: { feature: "web_recording", persistence_stage: "startup_cleanup" },
      })
    }

    const response = await createLiveRecordingSession(ctx, this.meetingId, this.contentType)

    if (!response.guid) {
      throw new Error(`API returned invalid response: missing guid. Got: ${JSON.stringify(response)}`)
    }

    this.sessionId = response.id
    this.sessionGuid = response.guid

    try {
      this.releaseLock = await acquireRecordingLock(this.sessionGuid)
      await createSession(this.sessionGuid, this.meetingId, this.contentType, this.sessionId, this.meetingTitle)
    } catch (error) {
      await this.releaseLock?.()
      await abandonSession(ctx, this.meetingId, this.sessionId).catch((cleanupError) => {
        Sentry.captureException(cleanupError, {
          tags: { feature: "web_recording", failure: "startup_cleanup" },
          extra: { meetingId: this.meetingId, sessionGuid: this.sessionGuid },
        })
      })
      throw error
    }

    this.emitProgress("recording")
  }

  async ingestChunk(blob: Blob): Promise<void> {
    if (this.aborted) return
    if (!this.sessionGuid || !this.sessionId) {
      throw new Error("Recording session not started")
    }
    const sessionGuid = this.sessionGuid

    // Sequence numbers are monotonic. Reusing a number after an overlapping write fails can overwrite
    // the identity of a chunk that another callback already persisted.
    const sequence = this.chunkSequence++

    const operation = this.ingestQueue.then(async () => {
      if (this.aborted) return
      if (this.ingestFailure) throw this.ingestFailure

      const chunkId = await this.saveChunkWithRecovery(sessionGuid, sequence, blob)
      this.persistedChunkCount++

      await this.updateSessionBestEffort({
        totalChunks: this.chunkSequence,
        lastActivityAt: new Date(),
      })

      await this.emitProgress("recording")
      this.enqueueUpload(chunkId, sequence, blob)
    })

    this.ingestQueue = operation.catch((error) => {
      this.ingestFailure ??=
        error instanceof RecordingPersistenceError ? error : this.buildPersistenceError(sequence, blob, error)
    })

    return operation
  }

  async finalize(capture: RecordingCaptureMetadata): Promise<RecordingFinalizationResult> {
    if (this.aborted) throw new Error("Upload aborted")
    if (!this.sessionGuid || !this.sessionId) {
      throw new Error("Recording session not started")
    }

    // MediaRecorder dispatches its final dataavailable event before stop. Waiting for this queue closes
    // the race where finalize snapshots pending chunks before that final Blob reaches IndexedDB.
    await this.ingestQueue

    const sequences = (await recordingDb.chunks.where({ sessionGuid: this.sessionGuid }).toArray()).map(
      (c) => c.sequence
    )
    capture = {
      ...capture,
      lost_sequences: Array.from({ length: this.chunkSequence }, (_, sequence) => sequence).filter(
        (s) => !sequences.includes(s)
      ),
    }
    // This is the durable end-of-capture marker. Recovery may upload automatically only after it exists.
    await updateSession(this.sessionGuid, { capture, totalChunks: this.chunkSequence, status: "uploading" })

    if (this.persistedChunkCount === 0) {
      // ingestChunk starts uploads in the background. Let any in-flight work settle before deleting
      // its IndexedDB row so the old uploader cannot retry against deleted data or emit late progress.
      await this.uploadQueue

      const ctx = await this.requireContext()
      await abandonSession(ctx, this.meetingId, this.sessionId)
      await this.completeLocalSession()
      await this.emitProgress("completed", {
        totalChunks: this.chunkSequence,
        uploadedChunks: 0,
        pendingChunks: 0,
      })
      return { sessionId: this.sessionId, outcome: "abandoned" }
    }

    await this.updateSessionBestEffort({ status: "uploading" })
    await this.emitProgress("uploading")

    await this.uploadQueue

    const pendingChunks = await getPendingChunks(this.sessionGuid)

    if (pendingChunks.length > 0) {
      const ctx = await this.requireContext()
      const chunksToUpload = pendingChunks.map((c) => ({
        id: c.id!,
        sequence: c.sequence,
        blob: c.blob,
      }))

      const result = await uploadChunkBatch(
        ctx,
        this.meetingId,
        this.sessionId,
        chunksToUpload,
        this.contentType,
        (progress: BatchUploadProgress) => {
          this.emitProgress("uploading", {
            uploadedChunks: this.chunkSequence - pendingChunks.length + progress.completed,
          })
        }
      )

      if (!result.success) {
        throw new Error(`Failed to upload ${result.failedCount} chunks`)
      }
    }

    await this.emitProgress("finalizing")

    const ctx = await this.requireContext()
    const response = await finalizeChunks(ctx, this.meetingId, this.sessionId, this.chunkSequence, capture)

    await this.completeLocalSession()
    await this.emitProgress("completed", {
      totalChunks: this.chunkSequence,
      uploadedChunks: this.persistedChunkCount,
      pendingChunks: 0,
    })

    return { sessionId: response.session_id, outcome: "finalized" }
  }

  async abort(): Promise<void> {
    this.aborted = true

    await this.ingestQueue
    await this.uploadQueue

    if (this.sessionGuid) {
      await updateSession(this.sessionGuid, { status: "failed" })
    }
  }

  async cleanup(): Promise<void> {
    this.aborted = true

    await this.ingestQueue
    await this.uploadQueue

    try {
      if (this.sessionGuid && this.sessionId) {
        await abandonSession(await this.requireContext(), this.meetingId, this.sessionId)
        await deleteSessionAndChunks(this.sessionGuid)
      }
    } finally {
      await this.release()
    }
  }

  async release(): Promise<void> {
    await this.releaseLock?.()
    this.releaseLock = undefined
  }

  getSessionId(): string | null {
    return this.sessionId
  }

  getSessionGuid(): string | null {
    return this.sessionGuid
  }

  getIngestFailure(): RecordingPersistenceError | null {
    return this.ingestFailure
  }

  isFull(): boolean {
    return this.full
  }

  private async requireContext(): Promise<ApiContext> {
    const ctx = await this.getContext()
    if (!ctx) throw new Error("No API context (auth) available")
    return ctx
  }

  private async saveChunkWithRecovery(sessionGuid: string, sequence: number, blob: Blob): Promise<number> {
    try {
      return await saveChunk(sessionGuid, sequence, blob)
    } catch (initialError) {
      const quotaExceeded = isQuotaExceededError(initialError)
      const storageBeforeRecovery = await estimateRecordingStorage()

      if (!quotaExceeded) {
        const error = this.buildPersistenceError(sequence, blob, initialError, storageBeforeRecovery)
        this.capturePersistenceFailure(error)
        throw error
      }

      let reclaimed = { chunks: 0, bytes: 0 }
      let reclaimError: unknown = null

      try {
        reclaimed = await reclaimUploadedChunkData()
      } catch (error) {
        reclaimError = error
      }

      try {
        const chunkId = await saveChunk(sessionGuid, sequence, blob)
        const diagnostics = await this.buildDiagnostics({
          sequence,
          blob,
          reclaimed,
          storageBeforeRecovery,
          initialError,
          reclaimError,
        })

        Sentry.captureException(initialError, {
          level: "warning",
          tags: {
            feature: "web_recording",
            persistence_stage: "save_chunk",
            quota_recovery: "succeeded",
          },
          extra: diagnostics,
        })

        return chunkId
      } catch (retryError) {
        const diagnostics = await this.buildDiagnostics({
          sequence,
          blob,
          reclaimed,
          storageBeforeRecovery,
          initialError,
          reclaimError,
          retryError,
        })
        const error = new RecordingPersistenceError(
          "The browser could not persist a recording chunk after reclaiming uploaded audio",
          diagnostics,
          retryError
        )
        this.capturePersistenceFailure(error)
        throw error
      }
    }
  }

  private enqueueUpload(chunkId: number, sequence: number, blob: Blob): void {
    const operation = this.uploadQueue.then(async () => {
      if (this.aborted || !this.sessionGuid || !this.sessionId) return

      const ctx = await this.getContext()
      if (!ctx) return

      const result = await uploadChunk(ctx, this.meetingId, this.sessionId, chunkId, sequence, blob, this.contentType)

      if (result.full) {
        this.full = true
        await this.emitProgress("recording")
        return
      }

      if (result.success) {
        this.lastConfirmedSequence = Math.max(this.lastConfirmedSequence, sequence)
        const counts = await getChunkCount(this.sessionGuid)
        await updateSession(this.sessionGuid, { uploadedChunks: counts.uploaded })

        if (result.final) {
          this.full = true
        }
      }

      await this.emitProgress("recording")
    })

    // A background upload failure must not poison the queue. The local row remains pending and finalize
    // retries it through uploadChunkBatch.
    this.uploadQueue = operation.catch((error) => {
      Sentry.captureException(error, {
        level: "warning",
        tags: { feature: "web_recording", persistence_stage: "background_upload" },
        extra: { sequence, sessionGuid: this.sessionGuid },
      })
    })
  }

  private async completeLocalSession(): Promise<void> {
    if (!this.sessionGuid) return
    await completeStoredSession(this.sessionGuid)
    await this.release()
  }

  private async updateSessionBestEffort(updates: Parameters<typeof updateSession>[1]): Promise<void> {
    if (!this.sessionGuid) return

    try {
      await updateSession(this.sessionGuid, updates)
    } catch (error) {
      Sentry.captureException(error, {
        level: "warning",
        tags: { feature: "web_recording", persistence_stage: "session_metadata" },
        extra: { sessionGuid: this.sessionGuid, updates },
      })
    }
  }

  private buildPersistenceError(
    sequence: number,
    blob: Blob,
    error: unknown,
    storageBeforeRecovery: RecordingPersistenceDiagnostics["storageBeforeRecovery"] = { usage: null, quota: null }
  ): RecordingPersistenceError {
    return new RecordingPersistenceError(
      "The browser could not persist a recording chunk",
      {
        sequence,
        blobBytes: blob.size,
        quotaExceeded: isQuotaExceededError(error),
        reclaimedChunks: 0,
        reclaimedBytes: 0,
        storageBeforeRecovery,
        storageAfterRecovery: storageBeforeRecovery,
        initialError: describeRecordingStorageError(error),
        reclaimError: null,
        retryError: null,
      },
      error
    )
  }

  private async buildDiagnostics(opts: {
    sequence: number
    blob: Blob
    reclaimed: { chunks: number; bytes: number }
    storageBeforeRecovery: RecordingPersistenceDiagnostics["storageBeforeRecovery"]
    initialError: unknown
    reclaimError: unknown
    retryError?: unknown
  }): Promise<RecordingPersistenceDiagnostics> {
    return {
      sequence: opts.sequence,
      blobBytes: opts.blob.size,
      quotaExceeded: true,
      reclaimedChunks: opts.reclaimed.chunks,
      reclaimedBytes: opts.reclaimed.bytes,
      storageBeforeRecovery: opts.storageBeforeRecovery,
      storageAfterRecovery: await estimateRecordingStorage(),
      initialError: describeRecordingStorageError(opts.initialError),
      reclaimError: opts.reclaimError ? describeRecordingStorageError(opts.reclaimError) : null,
      retryError: opts.retryError ? describeRecordingStorageError(opts.retryError) : null,
    }
  }

  private capturePersistenceFailure(error: RecordingPersistenceError): void {
    Sentry.captureException(error, {
      tags: {
        feature: "web_recording",
        persistence_stage: "save_chunk",
        quota_exceeded: String(error.diagnostics.quotaExceeded),
        quota_recovery: "failed",
      },
      extra: { ...error.diagnostics, meetingId: this.meetingId, sessionGuid: this.sessionGuid },
    })
  }

  private async emitProgress(status: UploadProgress["status"], overrides?: Partial<UploadProgress>): Promise<void> {
    if (!this.onProgress || !this.sessionGuid) return

    let counts: Awaited<ReturnType<typeof getChunkCount>>
    try {
      counts = await getChunkCount(this.sessionGuid)
    } catch {
      return
    }

    this.onProgress({
      totalChunks: counts.total,
      uploadedChunks: counts.uploaded,
      pendingChunks: counts.total - counts.uploaded,
      lastConfirmedSequence: this.lastConfirmedSequence,
      status,
      full: this.full,
      ...overrides,
    })
  }
}

export async function resumeUpload(
  meetingId: string,
  sessionId: string,
  sessionGuid: string,
  getContext: () => Promise<ApiContext | null>,
  contentType: string,
  onProgress?: (progress: BatchUploadProgress) => void,
  recoverInterrupted = false
): Promise<RecordingFinalizationResult> {
  const release = await acquireRecordingLock(sessionGuid)
  const { setRecordingTransferActive } = useMeetingRecordingStore.getState()
  setRecordingTransferActive(true)
  try {
    const ctx = await getContext()
    if (!ctx) throw new Error("No API context available")

    const session = await getSession(sessionGuid)
    if (!session) throw new Error("The local recording session is no longer available")
    if (!session.capture?.ended_at && !recoverInterrupted) {
      throw new Error("Capture did not finish. Confirm recovery of the saved audio first.")
    }
    const capture: RecordingCaptureMetadata = session.capture?.ended_at
      ? session.capture
      : {
          ...session.capture,
          ended_at: new Date().toISOString(),
          issues: [...(session.capture?.issues ?? []), "recovered_without_stop"],
        }

    const pendingChunks = await getPendingChunks(sessionGuid)

    if (pendingChunks.length > 0) {
      const chunksToUpload = pendingChunks.map((c) => ({
        id: c.id!,
        sequence: c.sequence,
        blob: c.blob,
      }))

      const result = await uploadChunkBatch(ctx, meetingId, sessionId, chunksToUpload, contentType, onProgress)

      if (!result.success) {
        throw new Error(`Failed to upload ${result.failedCount} chunks`)
      }
    }

    const counts = await getChunkCount(sessionGuid)

    const sequences = (await recordingDb.chunks.where({ sessionGuid }).toArray()).map((chunk) => chunk.sequence)
    const total = Math.max(session.totalChunks, counts.total, ...sequences.map((sequence) => sequence + 1))
    capture.lost_sequences = Array.from({ length: total }, (_, sequence) => sequence).filter(
      (s) => !sequences.includes(s)
    )
    await updateSession(sessionGuid, { capture, totalChunks: total, status: "uploading" })
    if (total === 0) await abandonSession(ctx, meetingId, sessionId)
    else await finalizeChunks(ctx, meetingId, sessionId, total, capture)
    await completeStoredSession(sessionGuid)

    return { sessionId, outcome: total === 0 ? "abandoned" : "finalized" }
  } finally {
    await release()
    setRecordingTransferActive(false)
  }
}

export async function discardStoredRecording(
  meetingId: string,
  sessionId: string,
  sessionGuid: string,
  getContext: () => Promise<ApiContext | null>
): Promise<void> {
  const release = await acquireRecordingLock(sessionGuid)
  try {
    const ctx = await getContext()
    if (!ctx) throw new Error("No API context available")
    await abandonSession(ctx, meetingId, sessionId)
    await deleteSessionAndChunks(sessionGuid)
  } finally {
    await release()
  }
}

async function completeStoredSession(sessionGuid: string): Promise<void> {
  try {
    await deleteSessionAndChunks(sessionGuid)
  } catch (cleanupError) {
    try {
      await updateSession(sessionGuid, { status: "completed", finalizedAt: new Date() })
    } catch (metadataError) {
      Sentry.captureException(metadataError, {
        level: "warning",
        tags: { feature: "web_recording", persistence_stage: "local_cleanup" },
        extra: {
          sessionGuid,
          cleanupError: describeRecordingStorageError(cleanupError),
        },
      })
    }
  }
}
